import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyNodeChanges,
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  type NodeChange,
  type ReactFlowInstance,
  type Edge,
  type Node,
  type OnNodeDrag,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTheme } from "next-themes";
import { toast } from "sonner";

import { useWorkspaceContext } from "./workspaceContext";
import { useWorkspaceAutoLayout } from "./useWorkspaceAutoLayout";
import { workspaceNodeTypes } from "./nodes/WorkspaceFlowNode";
import { workspaceEdgeTypes } from "./nodes/WorkspacePipelineEdge";
import {
  findParentCombinedPolicyNode,
  listCombinedPolicySourceNodes,
} from "./workspaceCombinedPolicyUtils";
import {
  layoutWorkspaceGraph,
  listExecutingAccountBridges,
} from "./layoutStrategyGraphSequential";
import {
  collectSelectionPathEdgeIds,
  toFlowEdges,
  toFlowNodes,
  type WorkspaceFlowNodeData,
} from "./workspaceGraphUtils";
import {
  isWorkspaceEphemeralNodeId,
  isWorkspacePlaceholderNodeId,
  isWorkspaceInsertNodeId,
  isAccountReportEdgeSkipped,
  toInsertStrategyFlowEdges,
  toInsertStrategyFlowNodes,
  toPlaceholderFlowEdges,
  toPlaceholderFlowNodes,
  type WorkspaceInsertNodeData,
  type WorkspacePlaceholderNodeData,
} from "./workspacePlaceholderUtils";
import {
  executingAccountNodeId,
  isWorkspaceExecutingAccountNodeId,
  toExecutingAccountContinuationFlowEdges,
  toExecutingAccountFlowEdges,
  toExecutingAccountFlowNodes,
  type WorkspaceExecutingAccountNodeData,
} from "./workspaceExecutingAccountUtils";
import type { WorkspaceExecutingAccountDrawerTarget } from "./workspaceDrawerEntry";
import type { WorkspaceLayoutMode } from "./workspaceLayoutMode";
import type { BrokerAccountRef } from "../../../utils/brokerAccountRef";
import { useWorkspacePositionSave } from "./useWorkspacePositionSave";
import {
  isDefaultWorkspaceViewport,
  useWorkspaceViewportSave,
} from "./useWorkspaceViewportSave";
import { useWorkspaceEntityTitles } from "./useWorkspaceReportTitles";
import { useWorkspaceEntityCardDetails } from "./useWorkspaceEntityCardDetails";
import { useWorkspaceSimulationSourceReportIds } from "./useWorkspaceSimulationSourceReportIds";
import {
  computeWorkspaceCanvasViewportForNodes,
  WORKSPACE_CANVAS_VIEWPORT_BOTTOM_PADDING,
  WORKSPACE_CANVAS_VIEWPORT_PINE_DOCK_BOTTOM_PADDING,
} from "./workspaceCanvasViewport";
import {
  WORKSPACE_NODE_SNAP_GRID,
} from "./workspaceGraphConstants";
import {
  resolveWorkspaceNodeDragPosition,
  syncPlaceholderPositionsWithCollisionTrap,
  WORKSPACE_NODE_COLLISION_GAP,
} from "./workspaceNodeCollision";
import { WorkspaceCanvasControls } from "./WorkspaceCanvasControls";
import { WorkspaceToolbar } from "./WorkspaceToolbar";
import {
  EMPTY_WORKSPACE_FOCUS_FILTER,
  isWorkspaceFocusFilterActive,
  collectWorkspaceFocusVisibleNodeIds,
  type WorkspaceFocusFilter,
} from "./workspaceFocusFilter";
import { useWorkspaceFocusEnrichment } from "./useWorkspaceFocusEnrichment";
import {
  WORKSPACE_PINE_DOCK_HEIGHT_PX,
  WorkspacePineDock,
  WorkspacePolicyPinePreviewDock,
} from "./WorkspacePineDock";
import {
  WORKSPACE_SIMULATION_POLICY_DOCK_HEIGHT_PX,
  WorkspaceSimulationPolicyPreviewDock,
} from "./WorkspaceSimulationPolicyPreviewDock";
import {
  WORKSPACE_PINE_DOCK_MOTION,
  WORKSPACE_PINE_DOCK_TRANSITION,
} from "./workspaceOverlayMotion";
import { useWorkspaceStrategyPine } from "./useWorkspaceStrategyPine";
import { useWorkspacePolicyPinePreview } from "./useWorkspacePolicyPinePreview";
import { useWorkspaceSimulationPolicyPreview } from "./useWorkspaceSimulationPolicyPreview";
import { dispatchWorkspacePolicyPinePreviewHide } from "./workspacePolicyPinePreviewEvents";
import { dispatchWorkspaceSimulationPolicyPreviewHide } from "./workspaceSimulationPolicyPreviewEvents";
import type { WorkspaceChildActionKind, WorkspaceChildAddMode } from "./workspaceChildActions";
import type { WorkspaceNode, WorkspaceViewport } from "../../../types/workspace";
import { WorkspaceCombineSelectionBar } from "./WorkspaceCombineSelectionBar";
import {
  canOfferCombinePolicies,
  combinablePolicyIdsForSelection,
  nextPolicyCombineSelection,
} from "./workspacePolicySelection";
import { useBillingContext } from "../../../contexts/BillingContext";

type WorkspaceCanvasFlowNode = Node<
  | WorkspaceFlowNodeData
  | WorkspacePlaceholderNodeData
  | WorkspaceInsertNodeData
  | WorkspaceExecutingAccountNodeData
>;

/** True when the API returned a non-default camera (user previously panned/zoomed/fit). */
function hasPersistedWorkspaceViewport(viewport: WorkspaceViewport | null | undefined): boolean {
  return !isDefaultWorkspaceViewport(viewport);
}

function applyWorkspaceCanvasViewport(
  instance: ReactFlowInstance<WorkspaceCanvasFlowNode, Edge>,
  nodes: WorkspaceCanvasFlowNode[],
  canvasWidth: number,
  canvasHeight: number,
  animate = true,
  bottomPadding: number = WORKSPACE_CANVAS_VIEWPORT_BOTTOM_PADDING
): void {
  if (nodes.length === 0 || canvasWidth <= 0 || canvasHeight <= 0) return;

  const viewport = computeWorkspaceCanvasViewportForNodes(nodes, canvasWidth, canvasHeight, {
    bottomPadding,
  });
  void instance.setViewport(viewport, { duration: animate ? 200 : 0 });
}

export type WorkspaceCanvasProps = {
  onAddChild?: (
    node: WorkspaceNode,
    kind: WorkspaceChildActionKind,
    mode?: WorkspaceChildAddMode
  ) => void;
  onCombinePolicies?: (node: WorkspaceNode) => void;
  /** Open combine dialog from ordered multi-select (first id = adopted report). */
  onCombineSelectedPolicies?: (policyNodeIds: string[]) => void;
  onRunCombinedSimulation?: (node: WorkspaceNode) => void;
  onRerunSimulation?: (node: WorkspaceNode) => void;
  onEditSimulationInSimulator?: (node: WorkspaceNode) => void;
  onInsertStrategyBetween?: (
    accountNode: WorkspaceNode,
    reportNode: WorkspaceNode,
    mode?: WorkspaceChildAddMode
  ) => void;
  onAddReportBranch?: (accountNode: WorkspaceNode, mode?: WorkspaceChildAddMode) => void;
  onOpenDetail?: (node: WorkspaceNode) => void;
  onOpenStrategyWorkspace?: (node: WorkspaceNode) => void;
  onOpenOnAccountCanvas?: (node: WorkspaceNode) => void;
  /** Open Tessera workspace hub for an executing-account diamond (after switching active account). */
  onOpenExecutingAccountWorkspace?: (account: BrokerAccountRef) => void;
  /** Open executing-account detail sheet. */
  onOpenExecutingAccountDetail?: (target: WorkspaceExecutingAccountDrawerTarget) => void;
  /** Unassign an executing account from its parent policy. */
  onRemoveExecutingAccount?: (
    parentPolicyNode: WorkspaceNode,
    account: BrokerAccountRef
  ) => void;
  /** Open policy drawer to assign executing accounts. */
  onAddExecutingAccount?: (node: WorkspaceNode) => void;
  mutating?: boolean;
  onToggleDisabled?: (node: WorkspaceNode) => void;
  onBreakLink?: (node: WorkspaceNode) => void;
  onDisconnect?: (node: WorkspaceNode) => void;
  onReparent?: (node: WorkspaceNode, newParent: WorkspaceNode) => void;
  /** Entity ids that should briefly flash neon aqua (e.g. report generation done). */
  completionFlashEntityIds?: ReadonlySet<string>;
  /** Node ids fading out after a failed optimistic create. */
  dismissingNodeIds?: ReadonlySet<string>;
  /** Called when the empty canvas is clicked — close sheets / node chrome. */
  onDismissOverlays?: () => void;
  /** In-canvas detail panels (mounted under the toolbar, not a fullscreen sheet). */
  drawerSlot?: React.ReactNode;
  /** Strategy entity id driving the pine dock (open strategy sheet or selected node). */
  pineStrategyEntityId?: string | null;
};

function WorkspaceCanvasInner({
  onAddChild,
  onCombinePolicies,
  onCombineSelectedPolicies,
  onRunCombinedSimulation,
  onRerunSimulation,
  onEditSimulationInSimulator,
  onInsertStrategyBetween,
  onAddReportBranch,
  onOpenDetail,
  onOpenStrategyWorkspace,
  onOpenOnAccountCanvas,
  onOpenExecutingAccountWorkspace,
  onOpenExecutingAccountDetail,
  onRemoveExecutingAccount,
  onAddExecutingAccount,
  mutating = false,
  onToggleDisabled,
  onBreakLink,
  onDisconnect,
  onReparent,
  completionFlashEntityIds,
  dismissingNodeIds,
  onDismissOverlays,
  drawerSlot,
  pineStrategyEntityId = null,
}: WorkspaceCanvasProps): React.ReactElement {
  const {
    detail,
    selectedNodeId,
    selectedNodeIds,
    setSelectedNodeId,
    setSelectedNodeIds,
    policyCombineMode,
    exitPolicyCombineMode,
    scope,
    patchNodePositions,
    viewMode,
    strategyName,
    contributingWorkspaces,
    layoutMode,
    setLayoutMode,
  } = useWorkspaceContext();
  const { simsPerReport } = useBillingContext();
  const { applyAutoLayout } = useWorkspaceAutoLayout();
  const { resolvedTheme } = useTheme();
  const flowRef = useRef<ReactFlowInstance<WorkspaceCanvasFlowNode, Edge> | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const persistScope = viewMode === "strategy" ? null : scope;
  const persistWorkspaceId = viewMode === "strategy" ? undefined : detail?.id;
  const savePositions = useWorkspacePositionSave(persistScope, persistWorkspaceId);
  const { saveViewport, seedSavedViewport, clearPendingViewportSave } = useWorkspaceViewportSave(
    persistScope,
    persistWorkspaceId
  );
  /** Skip persisting camera changes caused by restore / fit-to-nodes. */
  const suppressViewportSaveRef = useRef(false);
  /** Once per workspace open: restore saved camera or fit once. */
  const cameraInitializedForIdRef = useRef<string | null>(null);
  const entityTitles = useWorkspaceEntityTitles(detail?.nodes);
  const entityCardDetails = useWorkspaceEntityCardDetails(detail?.nodes, detail?.edges);
  const focusEnrichment = useWorkspaceFocusEnrichment(detail?.nodes);
  const [focusFilter, setFocusFilter] = useState<WorkspaceFocusFilter>(EMPTY_WORKSPACE_FOCUS_FILTER);

  useEffect(() => {
    setFocusFilter(EMPTY_WORKSPACE_FOCUS_FILTER);
  }, [detail?.id]);
  const simulationSourceReportIds = useWorkspaceSimulationSourceReportIds(
    detail?.nodes,
    detail?.edges
  );
  const [organizeLayoutPending, setOrganizeLayoutPending] = useState(false);

  const nodeActionsRef = useRef({
    onAddChild,
    onCombinePolicies,
    onRunCombinedSimulation,
    onRerunSimulation,
    onEditSimulationInSimulator,
    onInsertStrategyBetween,
    onAddReportBranch,
    onOpenDetail,
    onOpenStrategyWorkspace,
    onOpenOnAccountCanvas,
    onOpenExecutingAccountWorkspace,
    onOpenExecutingAccountDetail,
    onRemoveExecutingAccount,
    onAddExecutingAccount,
    onToggleDisabled,
    onBreakLink,
    onDisconnect,
    onReparent,
  });
  nodeActionsRef.current = {
    onAddChild,
    onCombinePolicies,
    onRunCombinedSimulation,
    onRerunSimulation,
    onEditSimulationInSimulator,
    onInsertStrategyBetween,
    onAddReportBranch,
    onOpenDetail,
    onOpenStrategyWorkspace,
    onOpenOnAccountCanvas,
    onOpenExecutingAccountWorkspace,
    onOpenExecutingAccountDetail,
    onRemoveExecutingAccount,
    onAddExecutingAccount,
    onToggleDisabled,
    onBreakLink,
    onDisconnect,
    onReparent,
  };

  const [actionsMenuNodeId, setActionsMenuNodeId] = useState<string | null>(null);

  const selectedStrategyEntityId = pineStrategyEntityId;

  const strategyPine = useWorkspaceStrategyPine(selectedStrategyEntityId);
  const policyPinePreview = useWorkspacePolicyPinePreview();
  const simulationPolicyPreview = useWorkspaceSimulationPolicyPreview();

  const selectedSimulationEntityId = useMemo(() => {
    if (!detail || !selectedNodeId) return null;
    const node = detail.nodes.find((entry) => entry.id === selectedNodeId);
    if (!node || (node.type !== "simulation" && node.type !== "combined_simulation")) {
      return null;
    }
    const entityId = node.entityId.trim();
    return entityId || null;
  }, [detail, selectedNodeId]);

  // Policy pine preview only while a non-strategy sheet is relevant; hide when
  // the user switches to a strategy node (strategy dock takes over).
  useEffect(() => {
    if (selectedStrategyEntityId && policyPinePreview.open) {
      dispatchWorkspacePolicyPinePreviewHide();
    }
  }, [policyPinePreview.open, selectedStrategyEntityId]);

  // Simulation policy card only while a simulation sheet is open.
  useEffect(() => {
    if (!selectedSimulationEntityId && simulationPolicyPreview.open) {
      dispatchWorkspaceSimulationPolicyPreviewHide();
    }
  }, [selectedSimulationEntityId, simulationPolicyPreview.open]);

  // Avoid stacking two left-half dock cards on the same canvas.
  useEffect(() => {
    if (selectedSimulationEntityId && policyPinePreview.open) {
      dispatchWorkspacePolicyPinePreviewHide();
    }
  }, [policyPinePreview.open, selectedSimulationEntityId]);

  const strategyPinePanelCount = useMemo(() => {
    if (!selectedStrategyEntityId) return 0;
    if (!strategyPine.strategy) {
      return strategyPine.loading ? 1 : 0;
    }
    let count = Number(strategyPine.showMaster) + Number(strategyPine.showActive);
    if (
      strategyPine.composeSessionActive &&
      strategyPine.showMaster &&
      !strategyPine.showActive
    ) {
      count = 2;
    }
    return count > 0 ? count : 0;
  }, [
    selectedStrategyEntityId,
    strategyPine.composeSessionActive,
    strategyPine.loading,
    strategyPine.showActive,
    strategyPine.showMaster,
    strategyPine.strategy,
  ]);

  const strategyPineDockReserve =
    Boolean(selectedStrategyEntityId) &&
    (strategyPine.showMaster ||
      strategyPine.showActive ||
      strategyPine.composeSessionActive);

  // Wait for strategy metadata before mounting the dock so master-only strategies
  // never paint a transient 2-column layout (master left) before snapping right.
  const showStrategyPineDock = strategyPineDockReserve && Boolean(strategyPine.strategy);
  const showPolicyPineDock = policyPinePreview.open && !selectedStrategyEntityId;
  const showSimulationPolicyDock =
    simulationPolicyPreview.open &&
    Boolean(selectedSimulationEntityId) &&
    !selectedStrategyEntityId &&
    !showPolicyPineDock;
  const [pineDockPresent, setPineDockPresent] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (showStrategyPineDock || showPolicyPineDock || showSimulationPolicyDock) {
      setPineDockPresent(true);
    }
  }, [showPolicyPineDock, showSimulationPolicyDock, showStrategyPineDock]);

  const keepPineDockSpace =
    showStrategyPineDock || showPolicyPineDock || showSimulationPolicyDock || pineDockPresent;

  const pineDockHeightPx = showSimulationPolicyDock
    ? WORKSPACE_SIMULATION_POLICY_DOCK_HEIGHT_PX
    : WORKSPACE_PINE_DOCK_HEIGHT_PX;

  const fitBottomPadding = keepPineDockSpace
    ? WORKSPACE_CANVAS_VIEWPORT_PINE_DOCK_BOTTOM_PADDING
    : WORKSPACE_CANVAS_VIEWPORT_BOTTOM_PADDING;

  // Keep padding in a ref so opening the pine dock does not recreate
  // fitCanvasToNodes and auto-refit the camera (which strands under-node menus).
  const fitBottomPaddingRef = useRef(fitBottomPadding);
  fitBottomPaddingRef.current = fitBottomPadding;

  const handleClearSelection = useCallback(() => {
    if (policyCombineMode) {
      exitPolicyCombineMode();
    } else {
      setSelectedNodeId(null);
    }
    setActionsMenuNodeId(null);
  }, [exitPolicyCombineMode, policyCombineMode, setSelectedNodeId]);

  const handleToggleEphemeralMenu = useCallback((nodeId: string) => {
    if (policyCombineMode) return;
    setSelectedNodeId(null);
    setActionsMenuNodeId((current) => (current === nodeId ? null : nodeId));
  }, [policyCombineMode, setSelectedNodeId]);

  const combinablePolicyIds = useMemo(() => {
    if (!detail || !policyCombineMode) return null;
    return combinablePolicyIdsForSelection(selectedNodeIds, detail.nodes, detail.edges);
  }, [detail, policyCombineMode, selectedNodeIds]);

  const showCombineSelectionBar = policyCombineMode;

  const focusPathNodeIds = useMemo(() => {
    if (!detail || !isWorkspaceFocusFilterActive(focusFilter)) return null;
    return collectWorkspaceFocusVisibleNodeIds(detail.nodes, detail.edges, focusFilter, {
      titles: {
        report: entityTitles.report,
        strategy: entityTitles.strategy,
        simulation: entityTitles.simulation,
      },
      enrichment: focusEnrichment,
      edges: detail.edges,
      nodes: detail.nodes,
    });
  }, [detail, entityTitles, focusEnrichment, focusFilter]);

  const focusHideMode =
    focusFilter.visibilityMode === "hide" && focusPathNodeIds != null;
  /** Dim mode: highlight matching paths; hide mode: drop the rest from the graph. */
  const focusMatchedNodeIds = focusHideMode ? null : focusPathNodeIds;
  const focusVisibleNodeIds = focusHideMode ? focusPathNodeIds : null;

  useEffect(() => {
    if (!focusVisibleNodeIds) return;
    if (
      selectedNodeId &&
      !focusVisibleNodeIds.has(selectedNodeId) &&
      !isWorkspaceExecutingAccountNodeId(selectedNodeId)
    ) {
      setSelectedNodeId(null);
      setActionsMenuNodeId(null);
    }
    if (selectedNodeIds.some((id) => !focusVisibleNodeIds.has(id))) {
      setSelectedNodeIds(selectedNodeIds.filter((id) => focusVisibleNodeIds.has(id)));
    }
  }, [focusVisibleNodeIds, selectedNodeId, selectedNodeIds, setSelectedNodeId, setSelectedNodeIds]);

  const focusHideLayoutKey = useMemo(() => {
    if (!focusHideMode || !focusVisibleNodeIds) return "";
    return Array.from(focusVisibleNodeIds).sort().join("|");
  }, [focusHideMode, focusVisibleNodeIds]);

  const graphOptions = useMemo(
    () => ({
      accountLabel: detail?.brokerAccountCode,
      mutating,
      actionsMenuNodeId: showCombineSelectionBar ? null : actionsMenuNodeId,
      viewMode,
      completionFlashEntityIds,
      dismissingNodeIds,
      selectedNodeIds,
      combinablePolicyIds,
      focusMatchedNodeIds,
      edges: detail?.edges,
      reportTitlesByEntityId: entityTitles.report,
      strategyTitlesByEntityId: entityTitles.strategy,
      simulationTitlesByEntityId: entityTitles.simulation,
      simulationSourceReportIdByEntityId: simulationSourceReportIds,
      entityCardDetailsByKey: entityCardDetails,
      onClearSelection: handleClearSelection,
      onSelectNode: setSelectedNodeId,
      onToggleEphemeralMenu: handleToggleEphemeralMenu,
      onAddChild: (
        node: WorkspaceNode,
        kind: WorkspaceChildActionKind,
        mode?: WorkspaceChildAddMode
      ) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onAddChild?.(node, kind, mode);
      },
      onCombinePolicies: (node: WorkspaceNode) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onCombinePolicies?.(node);
      },
      onRunCombinedSimulation: (node: WorkspaceNode) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onRunCombinedSimulation?.(node);
      },
      onRerunSimulation: (node: WorkspaceNode) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onRerunSimulation?.(node);
      },
      onEditSimulationInSimulator: (node: WorkspaceNode) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onEditSimulationInSimulator?.(node);
      },
      onInsertStrategyBetween: (
        accountNode: WorkspaceNode,
        reportNode: WorkspaceNode,
        mode?: WorkspaceChildAddMode
      ) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onInsertStrategyBetween?.(accountNode, reportNode, mode);
      },
      onAddReportBranch: (accountNode: WorkspaceNode, mode?: WorkspaceChildAddMode) => {
        setActionsMenuNodeId(null);
        nodeActionsRef.current.onAddReportBranch?.(accountNode, mode);
      },
      onOpenDetail: (node: WorkspaceNode) => nodeActionsRef.current.onOpenDetail?.(node),
      onOpenStrategyWorkspace: (node: WorkspaceNode) =>
        nodeActionsRef.current.onOpenStrategyWorkspace?.(node),
      onOpenOnAccountCanvas: (node: WorkspaceNode) =>
        nodeActionsRef.current.onOpenOnAccountCanvas?.(node),
      onAddExecutingAccount: (node: WorkspaceNode) =>
        nodeActionsRef.current.onAddExecutingAccount?.(node),
      onToggleDisabled: (node: WorkspaceNode) =>
        nodeActionsRef.current.onToggleDisabled?.(node),
      onDisconnect: (node: WorkspaceNode) => nodeActionsRef.current.onDisconnect?.(node),
      onReparent: (node: WorkspaceNode, newParent: WorkspaceNode) =>
        nodeActionsRef.current.onReparent?.(node, newParent),
      onBreakLink: (node: WorkspaceNode) => nodeActionsRef.current.onBreakLink?.(node),
    }),
    [
      actionsMenuNodeId,
      combinablePolicyIds,
      completionFlashEntityIds,
      dismissingNodeIds,
      detail?.brokerAccountCode,
      detail?.edges,
      entityTitles,
      entityCardDetails,
      focusMatchedNodeIds,
      handleClearSelection,
      handleToggleEphemeralMenu,
      mutating,
      selectedNodeIds,
      setSelectedNodeId,
      showCombineSelectionBar,
      simulationSourceReportIds,
      viewMode,
    ]
  );

  const graphOptionsRef = useRef(graphOptions);
  graphOptionsRef.current = graphOptions;

  const [flowNodes, setFlowNodes] = useState<WorkspaceCanvasFlowNode[]>([]);
  const [flowEdges, setFlowEdges] = useState<Edge[]>([]);
  const flowNodesRef = useRef(flowNodes);
  flowNodesRef.current = flowNodes;
  const detailEdgesRef = useRef(detail?.edges ?? []);
  detailEdgesRef.current = detail?.edges ?? [];

  const runWithSuppressedViewportSave = useCallback(
    (action: () => void) => {
      suppressViewportSaveRef.current = true;
      clearPendingViewportSave();
      action();
      // Cover restore/fit animation (200ms) plus a little settle time so
      // React Flow onMoveEnd cannot overwrite a real saved camera.
      window.setTimeout(() => {
        suppressViewportSaveRef.current = false;
      }, 450);
    },
    [clearPendingViewportSave]
  );

  const fitCanvasToNodes = useCallback(
    (animate = true) => {
      const canvas = viewportRef.current;
      const instance = flowRef.current;
      if (!canvas || !instance) return false;

      const nodes = instance.getNodes();
      if (nodes.length === 0) return false;

      const { width, height } = canvas.getBoundingClientRect();
      if (width <= 0 || height <= 0) return false;

      runWithSuppressedViewportSave(() => {
        applyWorkspaceCanvasViewport(
          instance,
          nodes,
          width,
          height,
          animate,
          fitBottomPaddingRef.current
        );
        seedSavedViewport(instance.getViewport());
      });
      return true;
    },
    [runWithSuppressedViewportSave, seedSavedViewport]
  );

  const restoreOrFitCamera = useCallback(
    (animate = false): boolean => {
      const instance = flowRef.current;
      const canvas = viewportRef.current;
      if (!instance || !canvas || !detail) return false;
      if (instance.getNodes().length === 0) return false;

      const { width, height } = canvas.getBoundingClientRect();
      if (width <= 0 || height <= 0) return false;

      if (hasPersistedWorkspaceViewport(detail.viewport)) {
        runWithSuppressedViewportSave(() => {
          void instance.setViewport(detail.viewport, { duration: animate ? 200 : 0 });
        });
        seedSavedViewport(detail.viewport);
        return true;
      }

      return fitCanvasToNodes(animate);
    },
    [detail, fitCanvasToNodes, runWithSuppressedViewportSave, seedSavedViewport]
  );

  // After hide-mode packing, fit the camera so the compact cluster is in view.
  // Leaving hide mode restores the saved/default camera for the full graph.
  const wasFocusHideRef = useRef(false);
  useEffect(() => {
    const leavingHide = wasFocusHideRef.current && !focusHideLayoutKey;
    wasFocusHideRef.current = Boolean(focusHideLayoutKey);

    if (!focusHideLayoutKey && !leavingHide) return;

    let cancelled = false;
    let attempts = 0;
    const tryCamera = (): void => {
      if (cancelled) return;
      const ok = leavingHide ? restoreOrFitCamera(true) : fitCanvasToNodes(true);
      if (ok) return;
      if (attempts++ >= 20) return;
      window.setTimeout(() => requestAnimationFrame(tryCamera), 32);
    };
    const timer = window.setTimeout(() => requestAnimationFrame(tryCamera), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [fitCanvasToNodes, focusHideLayoutKey, flowNodes.length, restoreOrFitCamera]);

  const handleOrganizeLayout = useCallback(async () => {
    if (!detail || detail.nodes.length <= 1 || organizeLayoutPending) return;
    setOrganizeLayoutPending(true);
    try {
      const ok = await applyAutoLayout(detail);
      if (ok) toast.success("Layout organized");
    } finally {
      setOrganizeLayoutPending(false);
    }
  }, [applyAutoLayout, detail, organizeLayoutPending]);

  const prevLayoutModeRef = useRef(layoutMode);
  useEffect(() => {
    if (viewMode !== "strategy") return;
    if (prevLayoutModeRef.current === layoutMode) return;
    prevLayoutModeRef.current = layoutMode;
    if (!detail || detail.nodes.length <= 1) return;

    let cancelled = false;
    void (async () => {
      const ok = await applyAutoLayout(detail);
      if (cancelled || !ok) return;
      // Fit after React Flow commits the new positions.
      window.setTimeout(() => {
        if (!cancelled) fitCanvasToNodes(true);
      }, 32);
    })();
    return () => {
      cancelled = true;
    };
  }, [applyAutoLayout, detail, fitCanvasToNodes, layoutMode, viewMode]);

  const handleLayoutModeChange = useCallback(
    (mode: WorkspaceLayoutMode) => {
      setLayoutMode(mode);
    },
    [setLayoutMode]
  );

  const skipFitAfterDragRef = useRef(false);

  useEffect(() => {
    cameraInitializedForIdRef.current = null;
  }, [detail?.id]);

  useEffect(() => {
    if (!detail || flowNodes.length === 0) return;
    if (cameraInitializedForIdRef.current === detail.id) return;

    if (skipFitAfterDragRef.current) {
      skipFitAfterDragRef.current = false;
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryInit = (): void => {
      if (cancelled) return;
      if (restoreOrFitCamera(attempts === 0)) {
        cameraInitializedForIdRef.current = detail.id;
        return;
      }
      if (attempts++ >= 30) return;
      window.setTimeout(() => {
        requestAnimationFrame(tryInit);
      }, 32);
    };

    // Wait a frame so React Flow can commit nodes, then retry until measured.
    const timer = window.setTimeout(() => requestAnimationFrame(tryInit), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [detail, flowNodes.length, restoreOrFitCamera]);

  const handleMoveEnd = useCallback(() => {
    if (suppressViewportSaveRef.current) return;
    if (!detail?.id || cameraInitializedForIdRef.current !== detail.id) return;
    const instance = flowRef.current;
    if (!instance) return;
    saveViewport(instance.getViewport());
  }, [detail?.id, saveViewport]);

  useEffect(() => {
    if (!detail) {
      setFlowNodes([]);
      setFlowEdges([]);
      return;
    }

    const visibleNodes =
      focusVisibleNodeIds == null
        ? detail.nodes
        : detail.nodes.filter((node) => focusVisibleNodeIds.has(node.id));
    const visibleEdges =
      focusVisibleNodeIds == null
        ? detail.edges
        : detail.edges.filter(
            (edge) =>
              focusVisibleNodeIds.has(edge.sourceNodeId) &&
              focusVisibleNodeIds.has(edge.targetNodeId)
          );

    // Hide mode: re-pack the visible subgraph so gaps from hidden nodes collapse.
    // Strategy Sequential re-runs on the filtered set (policies + exec-account bridges
    // stay in the focus path) so arms remain without leaving empty holes.
    const layoutNodes =
      focusVisibleNodeIds == null || visibleNodes.length === 0
        ? visibleNodes
        : (() => {
            const packed = layoutWorkspaceGraph(visibleNodes, visibleEdges, {
              viewMode,
              compact: true,
              layoutMode: viewMode === "strategy" ? layoutMode : "parallel",
              maxSimulationsPerReport: simsPerReport,
            });
            return visibleNodes.map((node) => {
              const position = packed.get(node.id);
              return position ? { ...node, position } : node;
            });
          })();

    const realNodes = toFlowNodes(layoutNodes, selectedNodeId, {
      ...graphOptions,
      edges: visibleEdges,
    });
    const placeholderNodes =
      viewMode === "strategy"
        ? []
        : toPlaceholderFlowNodes(layoutNodes, visibleEdges, {
            mutating: graphOptions.mutating,
            actionsMenuNodeId: graphOptions.actionsMenuNodeId,
            maxSimulationsPerReport: simsPerReport,
            onToggleMenu: graphOptions.onToggleEphemeralMenu,
            onAddChild: graphOptions.onAddChild,
            onRunCombinedSimulation: graphOptions.onRunCombinedSimulation,
          }).filter((node) => {
            const parentId = (node.data as { parentNode?: WorkspaceNode })?.parentNode?.id;
            return !parentId || focusVisibleNodeIds == null || focusVisibleNodeIds.has(parentId);
          });
    const insertNodes =
      viewMode === "strategy"
        ? []
        : toInsertStrategyFlowNodes(layoutNodes, visibleEdges, {
            mutating: graphOptions.mutating,
            actionsMenuNodeId: graphOptions.actionsMenuNodeId,
            onToggleMenu: graphOptions.onToggleEphemeralMenu,
            onAddStrategy: graphOptions.onInsertStrategyBetween,
            onAddReport: graphOptions.onAddReportBranch,
          }).filter((node) => {
            const accountId = (node.data as { accountNode?: WorkspaceNode })?.accountNode?.id;
            return !accountId || focusVisibleNodeIds == null || focusVisibleNodeIds.has(accountId);
          });
    const sequentialStory = viewMode === "strategy" && layoutMode === "sequential";
    const executingAccountNodes = toExecutingAccountFlowNodes(layoutNodes, visibleEdges, {
      viewMode,
      selectedNodeId,
      actionsMenuNodeId: graphOptions.actionsMenuNodeId,
      mutating: graphOptions.mutating,
      focusMatchedNodeIds,
      omitDiamondsForBridgeTargets: sequentialStory,
      maxSimulationsPerReport: simsPerReport,
      onOpenAccountWorkspace: (account) =>
        nodeActionsRef.current.onOpenExecutingAccountWorkspace?.(account),
      onCloseActionsMenu: () => setActionsMenuNodeId(null),
    }).filter((node) => {
      const parentId = (node.data as WorkspaceExecutingAccountNodeData).parentNode.id;
      return focusVisibleNodeIds == null || focusVisibleNodeIds.has(parentId);
    });
    const nextNodes: WorkspaceCanvasFlowNode[] = [
      ...realNodes,
      ...placeholderNodes,
      ...insertNodes,
      ...executingAccountNodes,
    ];

    setFlowNodes((current) => {
      if (current.length === 0) {
        return syncPlaceholderPositionsWithCollisionTrap(
          nextNodes,
          visibleEdges,
          WORKSPACE_NODE_COLLISION_GAP
        ) as WorkspaceCanvasFlowNode[];
      }

      const currentById = new Map(current.map((node) => [node.id, node]));
      const packingFocusHide = focusVisibleNodeIds != null;
      const merged = nextNodes.map((node) => {
        const existing = currentById.get(node.id);
        if (existing?.dragging) {
          return {
            ...node,
            position: existing.position,
            dragging: true,
          };
        }
        // When hide-packing, always take freshly laid-out positions for real nodes.
        if (packingFocusHide && !isWorkspaceEphemeralNodeId(node.id)) {
          return node;
        }
        if (existing && isWorkspaceEphemeralNodeId(node.id)) {
          return {
            ...node,
            position: existing.position,
          };
        }
        return node;
      });

      return syncPlaceholderPositionsWithCollisionTrap(
        merged,
        visibleEdges,
        WORKSPACE_NODE_COLLISION_GAP
      ) as WorkspaceCanvasFlowNode[];
    });

    const skippedEdgeIds = new Set(
      viewMode === "strategy"
        ? []
        : visibleEdges
            .filter((edge) => isAccountReportEdgeSkipped(edge, layoutNodes))
            .map((edge) => edge.id)
    );

    const combineMode = Boolean(
      combinablePolicyIds && combinablePolicyIds.size > 0 && selectedNodeIds.length > 0
    );
    const selectedIds =
      selectedNodeIds.length > 0
        ? selectedNodeIds
        : selectedNodeId
          ? [selectedNodeId]
          : [];
    const selectedCombinedPolicy =
      !combineMode && selectedIds.length === 1
        ? layoutNodes.find(
            (node) => node.id === selectedIds[0] && node.type === "combined_policy"
          ) ?? null
        : null;
    const selectedCombinedSimulation =
      !combineMode && selectedIds.length === 1
        ? layoutNodes.find(
            (node) => node.id === selectedIds[0] && node.type === "combined_simulation"
          ) ?? null
        : null;
    const combinedPolicyForSourceHighlight =
      selectedCombinedPolicy ??
      (selectedCombinedSimulation
        ? findParentCombinedPolicyNode(
            selectedCombinedSimulation,
            layoutNodes,
            visibleEdges
          )
        : null);
    const selectedPolicyOrCombined =
      !combineMode && selectedIds.length === 1
        ? layoutNodes.find(
            (node) =>
              node.id === selectedIds[0] &&
              (node.type === "policy" || node.type === "combined_policy")
          ) ?? null
        : null;
    const combinedSourceHighlight = combinedPolicyForSourceHighlight
      ? {
          combinedPolicyNodeId: combinedPolicyForSourceHighlight.id,
          sourcePolicyNodeIds: new Set(
            listCombinedPolicySourceNodes(
              combinedPolicyForSourceHighlight,
              layoutNodes,
              visibleEdges
            ).map((node) => node.id)
          ),
        }
      : null;
    const execFlowEdges = toExecutingAccountFlowEdges(layoutNodes, visibleEdges, {
      linkDirectlyToBridgeTargets: sequentialStory,
    });
    const execContinuationEdges = sequentialStory
      ? toExecutingAccountContinuationFlowEdges(
          layoutNodes,
          listExecutingAccountBridges(layoutNodes, visibleEdges).filter((bridge) => {
            const execId = executingAccountNodeId(bridge.fromPolicyNodeId, bridge.account);
            return executingAccountNodes.some((node) => node.id === execId);
          })
        )
      : [];
    // Soft EA edges participate in amber selection paths in every view mode
    // (not only Strategy Sequential) so policy ↔ executing-account links light up.
    const softSelectionPathEdges = [...execFlowEdges, ...execContinuationEdges].map((edge) => ({
      id: edge.id,
      sourceNodeId: edge.source,
      targetNodeId: edge.target,
    }));

    const selectedExecutingAccountId =
      !combineMode && selectedIds.length === 1 && isWorkspaceExecutingAccountNodeId(selectedIds[0]!)
        ? selectedIds[0]!
        : null;

    const selectionPathEdgeIds = (() => {
      if (selectedExecutingAccountId) {
        // Walk policy lineage via the soft policy→EA edge into this diamond.
        return collectSelectionPathEdgeIds(
          selectedExecutingAccountId,
          visibleEdges,
          softSelectionPathEdges
        );
      }
      if (selectedPolicyOrCombined) {
        return collectSelectionPathEdgeIds(
          selectedPolicyOrCombined.id,
          visibleEdges,
          softSelectionPathEdges
        );
      }
      if (selectedCombinedSimulation) {
        const ids = collectSelectionPathEdgeIds(
          selectedCombinedSimulation.id,
          visibleEdges,
          softSelectionPathEdges
        );
        if (combinedPolicyForSourceHighlight) {
          collectSelectionPathEdgeIds(
            combinedPolicyForSourceHighlight.id,
            visibleEdges,
            softSelectionPathEdges
          ).forEach((edgeId) => {
            ids.add(edgeId);
          });
        }
        return ids;
      }
      return null;
    })();

    const highlightSoftEdge = (edge: Edge): Edge => {
      if (!selectionPathEdgeIds?.has(edge.id)) return edge;
      return {
        ...edge,
        zIndex: 1001,
        data: {
          ...(edge.data as Record<string, unknown> | undefined),
          combinedSourceHighlight: true,
        },
      };
    };

    setFlowEdges([
      ...toFlowEdges(visibleEdges, layoutNodes, {
        skipEdgeIds: skippedEdgeIds,
        focusMatchedNodeIds,
        combinedSourceHighlight,
        selectionPathEdgeIds,
      }),
      ...execFlowEdges.map(highlightSoftEdge),
      // Diamonds only remain when the fed account is off-canvas; then continue diamond → account.
      ...execContinuationEdges.map(highlightSoftEdge),
      ...(viewMode === "strategy"
        ? []
        : [
            ...toPlaceholderFlowEdges(layoutNodes, visibleEdges),
            ...toInsertStrategyFlowEdges(layoutNodes, visibleEdges),
          ]),
    ]);
  }, [
    combinablePolicyIds,
    detail,
    focusMatchedNodeIds,
    focusVisibleNodeIds,
    graphOptions,
    layoutMode,
    selectedNodeId,
    selectedNodeIds,
    actionsMenuNodeId,
    viewMode,
    simsPerReport,
  ]);

  const onNodesChange = useCallback(
    (changes: NodeChange<WorkspaceCanvasFlowNode>[]) => {
      setFlowNodes((current) => {
        const previousById = new Map(current.map((node) => [node.id, node.position]));
        let next = applyNodeChanges(changes, current);
        // Resolve on every position touch — including drag-end (`dragging: false`),
        // otherwise the final pointer pose can snap into another node on release.
        const positionTouchedIds = new Set(
          changes
            .filter(
              (change): change is NodeChange<WorkspaceCanvasFlowNode> & { type: "position"; id: string } =>
                change.type === "position" && typeof change.id === "string"
            )
            .map((change) => change.id)
            .filter((id) => !isWorkspaceEphemeralNodeId(id))
        );

        if (positionTouchedIds.size > 0) {
          next = next.map((node) => {
            if (!positionTouchedIds.has(node.id)) return node;
            const resolved = resolveWorkspaceNodeDragPosition(
              node,
              next,
              WORKSPACE_NODE_COLLISION_GAP,
              previousById.get(node.id)
            );
            if (resolved.x === node.position.x && resolved.y === node.position.y) return node;
            return { ...node, position: resolved };
          });
        }

        const options = graphOptionsRef.current;
        const withCallbacks = next.map((node) => {
          if (isWorkspacePlaceholderNodeId(node.id)) {
            return {
              ...node,
              data: {
                ...(node.data as WorkspacePlaceholderNodeData),
                mutating: options.mutating,
                showActionsMenu: options.actionsMenuNodeId === node.id,
                onToggleMenu: () => options.onToggleEphemeralMenu?.(node.id),
                onAddChild: options.onAddChild,
                onRunCombinedSimulation: options.onRunCombinedSimulation,
              },
            };
          }

          if (isWorkspaceInsertNodeId(node.id)) {
            return {
              ...node,
              data: {
                ...(node.data as WorkspaceInsertNodeData),
                mutating: options.mutating,
                showActionsMenu: options.actionsMenuNodeId === node.id,
                onToggleMenu: () => options.onToggleEphemeralMenu?.(node.id),
                onAddStrategy: options.onInsertStrategyBetween,
                onAddReport: options.onAddReportBranch,
              },
            };
          }

          if (isWorkspaceExecutingAccountNodeId(node.id)) {
            const execData = node.data as WorkspaceExecutingAccountNodeData;
            const isSelected =
              options.actionsMenuNodeId === node.id ||
              options.selectedNodeIds?.includes(node.id);
            return {
              ...node,
              selected: Boolean(isSelected),
              data: {
                ...execData,
                isSelected: Boolean(isSelected),
                isParentSelected: selectedNodeIds[0] === execData.parentNode.id,
                showActionsMenu: options.actionsMenuNodeId === node.id,
                mutating: options.mutating,
                onOpenAccountWorkspace: (account: BrokerAccountRef) =>
                  nodeActionsRef.current.onOpenExecutingAccountWorkspace?.(account),
                onOpenDetail: () =>
                  nodeActionsRef.current.onOpenExecutingAccountDetail?.({
                    id: node.id,
                    account: execData.account,
                    label: execData.label,
                    parentPolicyNode: execData.parentNode,
                  }),
                onRemove: () =>
                  nodeActionsRef.current.onRemoveExecutingAccount?.(
                    execData.parentNode,
                    execData.account
                  ),
                onCloseActionsMenu: () => setActionsMenuNodeId(null),
              },
            };
          }

          return {
            ...node,
            data: {
              ...(node.data as WorkspaceFlowNodeData),
              accountLabel: options.accountLabel,
              mutating: options.mutating,
              onClearSelection: options.onClearSelection,
              onSelectNode: () => options.onSelectNode?.(node.id),
              onAddChild: options.onAddChild,
              onCombinePolicies: options.onCombinePolicies,
              onRunCombinedSimulation: options.onRunCombinedSimulation,
              onRerunSimulation: options.onRerunSimulation,
              onEditSimulationInSimulator: options.onEditSimulationInSimulator,
              onOpenDetail: options.onOpenDetail,
              onAddExecutingAccount: options.onAddExecutingAccount,
              onToggleDisabled: options.onToggleDisabled,
              canDisconnect: (node.data as WorkspaceFlowNodeData).canDisconnect,
              reparentCandidates: (node.data as WorkspaceFlowNodeData).reparentCandidates,
              onDisconnect: options.onDisconnect,
              onReparent: options.onReparent,
              onBreakLink: options.onBreakLink,
              selected: Boolean(options.selectedNodeIds?.includes(node.id)),
              combineSelectionAnchor: (node.data as WorkspaceFlowNodeData).combineSelectionAnchor,
              combineSelectionDimmed: (node.data as WorkspaceFlowNodeData).combineSelectionDimmed,
              combinedSourceHighlight: (node.data as WorkspaceFlowNodeData)
                .combinedSourceHighlight,
              showActionsMenu: node.id === actionsMenuNodeId,
              completionFlash: Boolean(
                options.completionFlashEntityIds?.has(
                  (node.data as WorkspaceFlowNodeData).workspaceNode.entityId.trim()
                )
              ),
              dismissing: Boolean(options.dismissingNodeIds?.has(node.id)),
            },
          };
        });

        // Follow parents when clear; pin add diamonds that would hit an obstacle.
        return syncPlaceholderPositionsWithCollisionTrap(
          withCallbacks,
          detailEdgesRef.current,
          WORKSPACE_NODE_COLLISION_GAP
        ) as WorkspaceCanvasFlowNode[];
      });
    },
    [actionsMenuNodeId, selectedNodeIds]
  );

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: { id: string; data?: unknown }) => {
      if (isWorkspaceExecutingAccountNodeId(node.id)) {
        event.stopPropagation();
        setSelectedNodeId(node.id);
        setActionsMenuNodeId(node.id);
        return;
      }

      if (isWorkspaceEphemeralNodeId(node.id)) return;
      event.stopPropagation();

      const workspaceNode = detail?.nodes.find((entry) => entry.id === node.id);
      if (!workspaceNode || !detail) {
        setSelectedNodeId(node.id);
        setActionsMenuNodeId(node.id);
        return;
      }

      // Combine mode: pick additional same-strategy policies / combined policies.
      if (policyCombineMode) {
        const isCombineCandidate =
          (workspaceNode.type === "policy" || workspaceNode.type === "combined_policy") &&
          !workspaceNode.isDisabled;
        if (!isCombineCandidate) {
          exitPolicyCombineMode();
          setSelectedNodeId(workspaceNode.id);
          setActionsMenuNodeId(workspaceNode.id);
          return;
        }

        const nextIds = nextPolicyCombineSelection(
          workspaceNode,
          selectedNodeIds,
          detail.nodes,
          detail.edges
        );
        setSelectedNodeIds(nextIds);
        setActionsMenuNodeId(null);
        onDismissOverlays?.();
        return;
      }

      // Normal mode: select + actions menu only. Open detail via "Open detail" in the menu.
      // Do not dismiss open drawers — stack allows a second sheet (e.g. report + simulation).
      setSelectedNodeId(workspaceNode.id);
      setActionsMenuNodeId(workspaceNode.id);
    },
    [
      detail,
      exitPolicyCombineMode,
      onDismissOverlays,
      policyCombineMode,
      selectedNodeIds,
      setSelectedNodeId,
      setSelectedNodeIds,
    ]
  );

  const dragOriginRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  const handleNodeDragStart: OnNodeDrag<WorkspaceCanvasFlowNode> = useCallback((_event, node) => {
    setActionsMenuNodeId(null);
    dragOriginRef.current.set(node.id, { x: node.position.x, y: node.position.y });
  }, []);

  /**
   * React Flow's `nodes` arg here is only the drag selection — not the canvas.
   * Always collide against the full flow graph via flowNodesRef / setState current.
   */
  const handleNodeDrag: OnNodeDrag<WorkspaceCanvasFlowNode> = useCallback((_event, node) => {
    if (isWorkspaceEphemeralNodeId(node.id)) return;
    const previous = dragOriginRef.current.get(node.id) ?? node.position;

    setFlowNodes((current) => {
      const withDesired = current.map((entry) =>
        entry.id === node.id ? { ...entry, position: node.position, dragging: true } : entry
      );
      const dragged = withDesired.find((entry) => entry.id === node.id);
      if (!dragged) return current;

      const resolved = resolveWorkspaceNodeDragPosition(
        dragged,
        withDesired,
        WORKSPACE_NODE_COLLISION_GAP,
        previous
      );
      dragOriginRef.current.set(node.id, resolved);

      const updated = withDesired.map((entry) =>
        entry.id === node.id ? { ...entry, position: resolved, dragging: true } : entry
      );
      return syncPlaceholderPositionsWithCollisionTrap(
        updated,
        detailEdgesRef.current,
        WORKSPACE_NODE_COLLISION_GAP
      ) as WorkspaceCanvasFlowNode[];
    });
  }, []);

  const handlePaneClick = useCallback(() => {
    if (policyCombineMode) {
      exitPolicyCombineMode();
    } else {
      setSelectedNodeId(null);
    }
    setActionsMenuNodeId(null);
    onDismissOverlays?.();
  }, [exitPolicyCombineMode, onDismissOverlays, policyCombineMode, setSelectedNodeId]);

  const handleNodeDragStop: OnNodeDrag<WorkspaceCanvasFlowNode> = useCallback(
    (_event, node) => {
      if (isWorkspaceEphemeralNodeId(node.id)) return;
      const previous = dragOriginRef.current.get(node.id);
      dragOriginRef.current.delete(node.id);

      // Prefer the live canvas graph — RF only passes the drag selection as `nodes`.
      const current = flowNodesRef.current;
      const withDesired = current.map((entry) =>
        entry.id === node.id ? { ...entry, position: node.position, dragging: false } : entry
      );
      const dragged = withDesired.find((entry) => entry.id === node.id) ?? {
        ...node,
        dragging: false,
      };
      const resolved = resolveWorkspaceNodeDragPosition(
        dragged,
        withDesired,
        WORKSPACE_NODE_COLLISION_GAP,
        previous
      );
      const finalNode = {
        ...dragged,
        position: resolved,
        dragging: false,
      };

      setFlowNodes(() => {
        const updated = withDesired.map((entry) =>
          entry.id === node.id ? { ...entry, position: resolved, dragging: false } : entry
        );
        return syncPlaceholderPositionsWithCollisionTrap(
          updated,
          detailEdgesRef.current,
          WORKSPACE_NODE_COLLISION_GAP
        ) as WorkspaceCanvasFlowNode[];
      });

      const positions = [{ nodeId: finalNode.id, x: resolved.x, y: resolved.y }];
      // Don't re-fit the camera when the user finishes a drag.
      skipFitAfterDragRef.current = true;
      patchNodePositions(positions);
      savePositions([finalNode as Node<WorkspaceFlowNodeData>], { immediate: true });
    },
    [patchNodePositions, savePositions]
  );

  if (!detail) {
    return (
      <div
        data-workspace-canvas-empty=""
        className="flex h-full min-h-[320px] flex-1 items-center justify-center p-8"
      >
        <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          Workspace graph unavailable.
        </p>
      </div>
    );
  }

  const isDark = resolvedTheme === "dark";
  const showEmptyHint = detail.nodes.length === 0;
  const focusFilterActive = isWorkspaceFocusFilterActive(focusFilter);

  return (
    <div data-workspace-canvas="" className="flex min-h-0 w-full flex-1 flex-col">
      <div
        ref={viewportRef}
        data-workspace-canvas-viewport=""
        data-workspace-focus-active={focusFilterActive ? "true" : undefined}
        className="relative min-h-0 w-full flex-1 overflow-hidden bg-muted/15"
        style={{ minHeight: "calc(100dvh - 4.5rem)" }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 p-3 md:p-4">
          <div className="pointer-events-auto">
            <WorkspaceToolbar
              variant="overlay"
              workspaceName={
                viewMode === "strategy"
                  ? strategyName?.trim() || detail.name
                  : detail.name
              }
              viewMode={viewMode}
              contributingAccountLabels={contributingWorkspaces
                .map((ws) => ws.brokerAccountCode?.trim() || ws.name?.trim() || "")
                .filter(Boolean)}
              onOrganizeLayout={handleOrganizeLayout}
              organizeLayoutDisabled={detail.nodes.length <= 1 || mutating}
              organizeLayoutPending={organizeLayoutPending}
              layoutMode={viewMode === "strategy" ? layoutMode : undefined}
              onLayoutModeChange={
                viewMode === "strategy" ? handleLayoutModeChange : undefined
              }
              focusFilter={focusFilter}
              onFocusFilterChange={setFocusFilter}
              focusFilterNodes={detail.nodes}
              focusEnrichment={focusEnrichment}
            />
          </div>
        </div>

        {showCombineSelectionBar ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center p-3 md:p-4">
            <WorkspaceCombineSelectionBar
              selectedCount={selectedNodeIds.length}
              canCombine={canOfferCombinePolicies(selectedNodeIds, detail.nodes, detail.edges)}
              onCombine={() => onCombineSelectedPolicies?.(selectedNodeIds)}
              onClear={handleClearSelection}
            />
          </div>
        ) : null}

        <div className="absolute inset-0">
          <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={workspaceNodeTypes}
          edgeTypes={workspaceEdgeTypes}
          onNodesChange={onNodesChange}
          onInit={(instance) => {
            flowRef.current = instance;
            // Restore or fit once React Flow is ready; effect retries if nodes aren't committed yet.
            requestAnimationFrame(() => {
              if (cameraInitializedForIdRef.current === detail.id) return;
              if (restoreOrFitCamera(false)) {
                cameraInitializedForIdRef.current = detail.id;
              }
            });
          }}
          onMoveEnd={handleMoveEnd}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          onNodeDragStart={handleNodeDragStart}
          onNodeDrag={handleNodeDrag}
          onNodeDragStop={handleNodeDragStop}
          nodesConnectable={false}
          elementsSelectable
          // Lattice snap is center-based in resolveWorkspaceNodeDragPosition —
          // React Flow's top-left snapToGrid fights that and skews mixed sizes.
          minZoom={0.15}
          maxZoom={1.75}
          proOptions={{ hideAttribution: true }}
          colorMode={isDark ? "dark" : "light"}
        >
          <Background
            gap={WORKSPACE_NODE_SNAP_GRID}
            size={1.25}
            variant={BackgroundVariant.Dots}
            className="!bg-transparent"
          />
          <WorkspaceCanvasControls
            fitBottomPadding={fitBottomPadding}
            bottomOffsetPx={
              keepPineDockSpace ? pineDockHeightPx + 56 : 12
            }
          />
        </ReactFlow>
        </div>

        <AnimatePresence onExitComplete={() => setPineDockPresent(false)}>
          {showStrategyPineDock ? (
            <motion.div
              key={selectedStrategyEntityId ?? "pine-dock"}
              data-workspace-pine-dock-shell=""
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[25] p-3 md:p-4"
              initial={reduceMotion ? false : WORKSPACE_PINE_DOCK_MOTION.initial}
              animate={WORKSPACE_PINE_DOCK_MOTION.animate}
              exit={reduceMotion ? undefined : WORKSPACE_PINE_DOCK_MOTION.exit}
              transition={reduceMotion ? { duration: 0 } : WORKSPACE_PINE_DOCK_TRANSITION}
            >
              <WorkspacePineDock pine={strategyPine} />
            </motion.div>
          ) : showPolicyPineDock ? (
            <motion.div
              key={`policy-pine-${policyPinePreview.policyVersionId ?? "preview"}`}
              data-workspace-pine-dock-shell=""
              data-workspace-policy-pine-dock-shell=""
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[25] p-3 md:p-4"
              initial={reduceMotion ? false : WORKSPACE_PINE_DOCK_MOTION.initial}
              animate={WORKSPACE_PINE_DOCK_MOTION.animate}
              exit={reduceMotion ? undefined : WORKSPACE_PINE_DOCK_MOTION.exit}
              transition={reduceMotion ? { duration: 0 } : WORKSPACE_PINE_DOCK_TRANSITION}
            >
              <WorkspacePolicyPinePreviewDock preview={policyPinePreview} />
            </motion.div>
          ) : showSimulationPolicyDock ? (
            <motion.div
              key={`simulation-policy-${simulationPolicyPreview.simulationId ?? "preview"}`}
              data-workspace-pine-dock-shell=""
              data-workspace-simulation-policy-dock-shell=""
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[25] p-3 md:p-4"
              initial={reduceMotion ? false : WORKSPACE_PINE_DOCK_MOTION.initial}
              animate={WORKSPACE_PINE_DOCK_MOTION.animate}
              exit={reduceMotion ? undefined : WORKSPACE_PINE_DOCK_MOTION.exit}
              transition={reduceMotion ? { duration: 0 } : WORKSPACE_PINE_DOCK_TRANSITION}
            >
              <WorkspaceSimulationPolicyPreviewDock preview={simulationPolicyPreview} />
            </motion.div>
          ) : null}
        </AnimatePresence>

        {drawerSlot && React.isValidElement(drawerSlot)
          ? React.cloneElement(
              drawerSlot as React.ReactElement<{
                bottomReservePx?: number;
                strategyPinePanelCount?: number;
              }>,
              {
                bottomReservePx:
                  showStrategyPineDock || showPolicyPineDock
                    ? WORKSPACE_PINE_DOCK_HEIGHT_PX
                    : 0,
                strategyPinePanelCount:
                  strategyPinePanelCount > 0 ? strategyPinePanelCount : undefined,
              }
            )
          : drawerSlot}

        {showEmptyHint ? (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 z-[1] flex -translate-y-1/2 justify-center px-4">
            <div className="max-w-md rounded-xl border border-amber-500/40 bg-card/90 px-4 py-3 text-center shadow-sm backdrop-blur">
              <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
                No nodes returned for this workspace. The backend should create one account node on
                workspace create.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function WorkspaceCanvas(props: WorkspaceCanvasProps): React.ReactElement {
  return (
    <div data-workspace-canvas-root="" className="flex min-h-0 w-full flex-1 flex-col">
      <ReactFlowProvider>
        <WorkspaceCanvasInner {...props} />
      </ReactFlowProvider>
    </div>
  );
}
